/** Вход: Mini App, гость, виджет Telegram. */
import type { EndpointDef } from "../endpoint";
import { Locale } from "../../common";
import { PlayerState } from "../player";
import { z } from "zod";

/** Метка UTM или хост: непонятное значение отбрасывается, вход из-за него не падает. */
const LandingTag = (re: RegExp) => z.string().regex(re).optional().catch(undefined);
const UTM_RE = /^[A-Za-z0-9_.-]{1,64}$/;

/** Откуда пришли на страницу: UTM адреса и хост внешнего referrer. Нужно только при создании игрока. */
export const Landing = z.object({
  utmSource: LandingTag(UTM_RE),
  utmCampaign: LandingTag(UTM_RE),
  utmContent: LandingTag(UTM_RE),
  referrer: LandingTag(/^[a-z0-9.-]{1,100}$/),
  /** `navigator.webdriver`: страницу ведёт Playwright/Selenium — игрок помечается проверкой (ISSUE-131). */
  automated: z.literal(true).optional().catch(undefined),
});
export type Landing = z.infer<typeof Landing>;

/** Вход в Mini App: `Telegram.WebApp.initData` как есть (сервер проверяет HMAC с ключом `WebAppData`). */
export const AuthTelegramRequest = z.object({
  initData: z.string().min(1).max(4096),
  /** `start_param` из `?startapp=` — источник аккаунта и переход сразу в бой. */
  startParam: z.string().max(64).optional(),
  landing: Landing.optional(),
});

/** Гость в браузере: cookie-сессия без Telegram. */
export const AuthGuestRequest = z.object({
  locale: Locale.optional(),
  landing: Landing.optional(),
});

/** «Войти через Telegram» на сайте (данные виджета Telegram Login). Гостевой прогресс сливается с аккаунтом. */
export const AuthTelegramLoginRequest = z.object({
  id: z.number().int().positive(),
  first_name: z.string(),
  last_name: z.string().optional(),
  username: z.string().optional(),
  photo_url: z.string().optional(),
  auth_date: z.number().int().positive(),
  hash: z.string().regex(/^[0-9a-f]{64}$/),
  /** Не входит в подпись виджета. */
  landing: Landing.optional(),
});
export type AuthTelegramLoginRequest = z.infer<typeof AuthTelegramLoginRequest>;

/** Ответ входа. */
/** Что уже было у игрока за всё время, на любом устройстве: «первые» события аналитики второй раз не шлём. */
export const PlayerHistory = z.object({
  bossKilled: z.boolean(),
  lost: z.boolean(),
});
export type PlayerHistory = z.infer<typeof PlayerHistory>;

export const NO_HISTORY: PlayerHistory = { bossKilled: false, lost: false };

export const AuthResponse = z.object({
  state: PlayerState,
  /** Аккаунт создан этим запросом. */
  created: z.boolean(),
  /** Гостевой прогресс слит в аккаунт Telegram (только telegram-login). */
  merged: z.boolean(),
  history: PlayerHistory,
});
export type AuthResponse = z.infer<typeof AuthResponse>;

export const authEndpoints = {
  /** Вход в Mini App по initData. */
  authTelegram: {
    method: "POST",
    path: "/api/auth/telegram",
    request: AuthTelegramRequest,
    response: AuthResponse,
    auth: false,
  },
  /** Гость в браузере (или продление гостевой сессии). */
  authGuest: {
    method: "POST",
    path: "/api/auth/guest",
    request: AuthGuestRequest,
    response: AuthResponse,
    auth: false,
  },
  /** Вход через виджет Telegram Login на сайте со слиянием гостевого прогресса. */
  authTelegramLogin: {
    method: "POST",
    path: "/api/auth/telegram-login",
    request: AuthTelegramLoginRequest,
    response: AuthResponse,
    auth: false,
  },
} as const satisfies Record<string, EndpointDef>;
